import React, { useState, useMemo } from "react";
import "./UserSelector.scss";

/**
 * UserSelector — original dropdown style, enhanced functionality
 *
 * Features:
 *  - Same native <select> dropdowns for Region & Outlet as before
 *  - Multi-region support via a secondary "Also include region" dropdown
 *  - Search input to filter users by name
 *  - Selected count badge + Clear All
 *  - Show/hide removable selected-user tags
 *  - Quick-select buttons per outlet
 *  - Highlighted selected users in the checkbox grid
 *
 * Props:
 *   regionData    : { [region]: { [outlet]: [{ id, username }] } }
 *   selectedUsers : string[]
 *   onChange      : (ids: string[]) => void
 */
const UserSelector = ({ regionData = {}, selectedUsers = [], onChange }) => {
  const [search, setSearch] = useState("");
  const [primaryRegion, setPrimaryRegion] = useState("All Regions");
  const [extraRegions, setExtraRegions] = useState([]); // additional regions
  const [selectedDistrict, setSelectedDistrict] = useState("All Outlet");
  const [showTags, setShowTags] = useState(false);

  // ── Derived data ───────────────────────────────────────────────────────────

  const allRegionNames = useMemo(
    () => Object.keys(regionData).filter((r) => r !== "All Regions"),
    [regionData],
  );

  // The full set of active regions
  const activeRegions = useMemo(() => {
    if (primaryRegion === "All Regions") return allRegionNames;
    return [primaryRegion, ...extraRegions];
  }, [primaryRegion, extraRegions, allRegionNames]);

  // Regions available to add as extra (not already active)
  const addableRegions = useMemo(() => {
    if (primaryRegion === "All Regions") return [];
    return allRegionNames.filter(
      (r) => r !== primaryRegion && !extraRegions.includes(r),
    );
  }, [primaryRegion, extraRegions, allRegionNames]);

  const districts = useMemo(() => {
    const set = new Set();
    activeRegions.forEach((r) => {
      Object.keys(regionData[r] || {}).forEach((o) => set.add(o));
    });
    return [...set].sort();
  }, [activeRegions, regionData]);

  const getUsersForScope = (regions, district) => {
    let users = [];
    regions.forEach((r) => {
      const regionObj = regionData[r] || {};
      const outlets =
        district === "All Outlet"
          ? Object.keys(regionObj)
          : regionObj[district]
            ? [district]
            : [];
      outlets.forEach((o) => {
        (regionObj[o] || []).forEach((u) => users.push(u));
      });
    });
    const seen = new Set();
    return users.filter((u) => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    });
  };

  const scopedUsers = useMemo(
    () => getUsersForScope(activeRegions, selectedDistrict),
    [regionData, activeRegions, selectedDistrict],
  );

  const userList = useMemo(() => {
    if (!search.trim()) return scopedUsers;
    const q = search.trim().toLowerCase();
    return scopedUsers.filter((u) => u.username.toLowerCase().includes(q));
  }, [scopedUsers, search]);

  const allUsers = useMemo(() => {
    const seen = new Set();
    return Object.values(regionData)
      .flatMap((r) => Object.values(r || {}))
      .flat()
      .filter((u) => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
  }, [regionData]);

  const selectedSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);

  const allChecked =
    userList.length > 0 && userList.every((u) => selectedSet.has(u.id));

  const outletGroups = useMemo(() => {
    if (selectedDistrict !== "All Outlet") return [];
    const map = {};
    activeRegions.forEach((r) => {
      const regionObj = regionData[r] || {};
      Object.entries(regionObj).forEach(([outlet, users]) => {
        if (!map[outlet]) map[outlet] = [];
        users.forEach((u) => map[outlet].push(u));
      });
    });
    // Deduplicate users per outlet
    return Object.entries(map).map(([outlet, users]) => {
      const seen = new Set();
      const deduped = users.filter((u) => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
      return {
        outlet,
        users: deduped,
        allSelected:
          deduped.length > 0 && deduped.every((u) => selectedSet.has(u.id)),
      };
    });
  }, [regionData, activeRegions, selectedDistrict, selectedSet]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePrimaryRegionChange = (e) => {
    const region = e.target.value;
    setPrimaryRegion(region);
    setExtraRegions([]);
    setSelectedDistrict("All Outlet");
    setSearch("");

    const regions = region === "All Regions" ? allRegionNames : [region];
    onChange(getUsersForScope(regions, "All Outlet").map((u) => u.id));
  };

  const handleAddExtraRegion = (e) => {
    const region = e.target.value;
    if (!region) return;
    const next = [...extraRegions, region];
    setExtraRegions(next);
    setSelectedDistrict("All Outlet");
    setSearch("");
    const regions = [primaryRegion, ...next];
    onChange(getUsersForScope(regions, "All Outlet").map((u) => u.id));
    // Reset the select back to placeholder
    e.target.value = "";
  };

  const removeExtraRegion = (region) => {
    const next = extraRegions.filter((r) => r !== region);
    setExtraRegions(next);
    setSelectedDistrict("All Outlet");
    const regions = [primaryRegion, ...next];
    onChange(getUsersForScope(regions, "All Outlet").map((u) => u.id));
  };

  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setSelectedDistrict(district);
    setSearch("");
    onChange(getUsersForScope(activeRegions, district).map((u) => u.id));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newIds = userList
        .map((u) => u.id)
        .filter((id) => !selectedSet.has(id));
      onChange([...selectedUsers, ...newIds]);
    } else {
      const listIds = new Set(userList.map((u) => u.id));
      onChange(selectedUsers.filter((id) => !listIds.has(id)));
    }
  };

  const handleUserChange = (e) => {
    const { value, checked } = e.target;
    onChange(
      checked
        ? [...selectedUsers, value]
        : selectedUsers.filter((id) => id !== value),
    );
  };

  const handleQuickSelectOutlet = (users, allSelected) => {
    if (allSelected) {
      const ids = new Set(users.map((u) => u.id));
      onChange(selectedUsers.filter((id) => !ids.has(id)));
    } else {
      const newIds = users
        .map((u) => u.id)
        .filter((id) => !selectedSet.has(id));
      onChange([...selectedUsers, ...newIds]);
    }
  };

  const removeUser = (id) =>
    onChange(selectedUsers.filter((uid) => uid !== id));
  const clearAll = () => onChange([]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="us-root">
      {/* ── Region dropdown (same as original) ── */}
      <div className="region-dropdown">
        <label>Region:</label>
        <select value={primaryRegion} onChange={handlePrimaryRegionChange}>
          <option value="All Regions">All Regions</option>
          {allRegionNames.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {/* ── Extra regions (only shown when a specific region is selected) ── */}
      {primaryRegion !== "All Regions" && (
        <div className="region-dropdown">
          <label>Also include:</label>
          <div className="us-extra-regions">
            {/* Tags for already-added extra regions */}
            {extraRegions.map((r) => (
              <span key={r} className="us-extra-tag">
                {r}
                <button
                  type="button"
                  className="us-extra-tag-remove"
                  onClick={() => removeExtraRegion(r)}
                >
                  ×
                </button>
              </span>
            ))}
            {/* Dropdown to add another region */}
            {addableRegions.length > 0 && (
              <select
                className="us-add-region-select"
                defaultValue=""
                onChange={handleAddExtraRegion}
              >
                <option value="" disabled>
                  + Add region…
                </option>
                {addableRegions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* ── Outlet dropdown (same as original) ── */}
      {districts.length > 0 && (
        <div className="district-dropdown">
          <label>Outlet (District):</label>
          <select
            value={selectedDistrict}
            onChange={handleDistrictChange}
            disabled={
              primaryRegion === "All Regions" && extraRegions.length === 0
            }
          >
            <option value="All Outlet">All Outlet</option>
            {districts.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Quick-select per outlet ── */}
      {outletGroups.length > 0 && (
        <div className="us-quick-select-wrap">
          <span className="us-quick-label">Quick select by outlet:</span>
          <div className="us-quick-buttons">
            {outletGroups.map(({ outlet, users, allSelected }) => (
              <button
                key={outlet}
                type="button"
                className={`us-quick-btn ${allSelected ? "us-quick-btn--active" : ""}`}
                onClick={() => handleQuickSelectOutlet(users, allSelected)}
                title={`${allSelected ? "Deselect" : "Select"} all ${users.length} from ${outlet}`}
              >
                {allSelected ? "✓ " : ""}
                {outlet}
                <span className="us-quick-count">{users.length}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Count badge + Clear All + Show tags ── */}
      {selectedUsers.length > 0 && (
        <div className="us-count-bar">
          <span className="us-count-badge">
            {selectedUsers.length} staff selected
          </span>
          <button type="button" className="us-clear-btn" onClick={clearAll}>
            Clear all
          </button>
          <button
            type="button"
            className="us-toggle-tags-btn"
            onClick={() => setShowTags((v) => !v)}
          >
            {showTags ? "Hide selected ▲" : "Show selected ▼"}
          </button>
        </div>
      )}

      {/* ── Removable tags ── */}
      {showTags && selectedUsers.length > 0 && (
        <div className="us-tags-wrap">
          {selectedUsers.map((id) => {
            const user = allUsers.find((u) => u.id === id);
            return (
              <span key={id} className="us-tag">
                {user ? user.username : id}
                <button
                  type="button"
                  className="us-tag-remove"
                  onClick={() => removeUser(id)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* ── Search input ── */}
      <div className="us-search-wrap">
        <input
          className="us-search-input"
          type="text"
          placeholder="Search staff by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="us-search-clear"
            onClick={() => setSearch("")}
          >
            ×
          </button>
        )}
      </div>

      {/* ── User checkbox grid (same as original) ── */}
      <div className="user-dropdown">
        <label>
          <input
            type="checkbox"
            value="ALL"
            checked={allChecked}
            onChange={handleSelectAll}
          />
          Select All
        </label>
        {userList.map((user) => (
          <label
            key={user.id}
            className={selectedSet.has(user.id) ? "us-user-selected" : ""}
          >
            <input
              type="checkbox"
              value={user.id}
              checked={selectedSet.has(user.id)}
              onChange={handleUserChange}
            />
            {user.username}
          </label>
        ))}
        {userList.length === 0 && search.trim() && (
          <span className="us-no-results">No staff match "{search}"</span>
        )}
        {userList.length === 0 && !search.trim() && (
          <span className="us-no-results">No staff in selected scope.</span>
        )}
      </div>
    </div>
  );
};

export default UserSelector;
